import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import type { components } from '@trackercop/contracts';
import { apiClient } from '@/lib/api-client';
import { queryKeys } from '@/lib/query-keys';
import { Table, THead, TBody, TR, TH, TD } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ReviewPopover } from '@/components/review-popover';
import { formatDateTime } from '@/lib/format';

type GTMContainer = components['schemas']['GTMContainer'];
type GTMTag = components['schemas']['GTMTag'];
type ReviewMap = Map<string, components['schemas']['FindingReview']>;

interface GtmTabProps {
  crawlId: string;
  containers: GTMContainer[];
  onContainerSelected: (containerId: string) => void;
  reviews: ReviewMap;
}

export function GtmTab({ crawlId, containers, onContainerSelected, reviews }: GtmTabProps) {
  const [expanded, setExpanded] = useState<string | null>(null);

  const tagsQuery = useQuery({
    queryKey: queryKeys.crawls.gtmTags(crawlId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET('/crawls/{crawlId}/gtm/tags', {
        params: { path: { crawlId } },
      });
      if (error) throw error;
      return data;
    },
  });

  const tags = tagsQuery.data?.items ?? [];

  if (containers.length === 0) {
    return <p className="text-sm text-muted-foreground">No GTM containers detected on this crawl.</p>;
  }

  return (
    <div className="flex flex-col gap-3">
      {containers.map((container) => {
        const containerTags = tags.filter((t) => t.gtmContainerId === container.id);
        const open = expanded === container.id;
        return (
          <Card key={container.id}>
            <CardHeader className="flex-row items-center justify-between">
              <CardTitle>
                {container.containerId}
                {container.version && (
                  <span className="ml-2 text-xs font-normal text-muted-foreground">v{container.version}</span>
                )}
              </CardTitle>
              <div className="flex items-center gap-2">
                <Badge variant="muted" title={container.pageIds?.join('\n')}>
                  {container.pageCount ?? 1} {(container.pageCount ?? 1) === 1 ? 'page' : 'pages'}
                </Badge>
                <Badge variant="muted">{containerTags.length} tags</Badge>
                <button
                  className="text-xs underline underline-offset-4"
                  onClick={() => setExpanded(open ? null : container.id)}
                >
                  {open ? 'Hide' : 'View'} tags
                </button>
              </div>
            </CardHeader>
            {open && (
              <CardContent className="flex flex-col gap-3">
                <button
                  className="w-fit text-xs text-primary underline underline-offset-4"
                  onClick={() => onContainerSelected(container.id)}
                >
                  Filter Scripts / Cookies / Payloads tabs to this container
                </button>
                <ContainerTagTable tags={containerTags} crawlId={crawlId} reviews={reviews} />
                <ContainerInventory crawlId={crawlId} containerId={container.id} />
              </CardContent>
            )}
          </Card>
        );
      })}
    </div>
  );
}

function ContainerTagTable({
  tags,
  crawlId,
  reviews,
}: {
  tags: GTMTag[];
  crawlId: string;
  reviews: ReviewMap;
}) {
  if (tags.length === 0) return <p className="text-xs text-muted-foreground">No tags recorded for this container.</p>;
  return (
    <Table>
      <THead>
        <TR>
          <TH>Tag name</TH>
          <TH>Type</TH>
          <TH>Vendor</TH>
          <TH>Fired</TH>
          <TH>At</TH>
          <TH>Review</TH>
        </TR>
      </THead>
      <TBody>
        {tags.map((t) => {
          const review = reviews.get(`gtm_tag:${t.id}`);
          return (
            <TR key={t.id}>
              <TD className="text-xs">{t.tagName ?? '—'}</TD>
              <TD className="text-xs">{t.tagType ?? '—'}</TD>
              <TD className="text-xs">{t.vendor ?? '—'}</TD>
              <TD>{t.fired ? <Badge variant="success">Fired</Badge> : <Badge variant="muted">Quiet</Badge>}</TD>
              <TD className="text-xs">{t.firedAtMs ? formatDateTime(new Date(t.firedAtMs).toISOString()) : '—'}</TD>
              <TD>
                <ReviewPopover
                  crawlId={crawlId}
                  entityType="gtm_tag"
                  entityId={t.id}
                  currentStatus={review?.status ?? 'unreviewed'}
                  currentNote={review?.note ?? null}
                />
              </TD>
            </TR>
          );
        })}
      </TBody>
    </Table>
  );
}

function ContainerInventory({ crawlId, containerId }: { crawlId: string; containerId: string }) {
  const query = useQuery({
    queryKey: queryKeys.crawls.gtmInventory(crawlId, containerId),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        '/crawls/{crawlId}/gtm/containers/{containerId}/inventory',
        { params: { path: { crawlId, containerId } } },
      );
      if (error) throw error;
      return data;
    },
  });
  if (query.isLoading) return <p className="text-xs text-muted-foreground">Loading inventory…</p>;
  if (query.isError || !query.data) return null;
  const s = query.data.summary;
  return (
    <div className="grid grid-cols-3 gap-3">
      <InventoryStat label="Scripts" value={s.scriptCount} />
      <InventoryStat label="Cookies" value={s.cookieCount} />
      <InventoryStat label="Payloads" value={s.payloadCount} />
    </div>
  );
}

function InventoryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border bg-muted/20 p-3">
      <div className="text-[10px] uppercase text-muted-foreground">{label}</div>
      <div className="text-xl font-semibold">{value}</div>
    </div>
  );
}
